import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Clock, Calendar } from 'lucide-react';
import { timeConditionService } from '../services';
import { messageHandler } from '../services/message-handler.service';
import type { TimeCondition, TimeException } from '../types/timeCondition';
import PageHeader from '../components/ui/PageHeader';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import TimeConditionList from '../components/time-conditions/TimeConditionList';
import TimeConditionForm from '../components/time-conditions/TimeConditionForm';
import ExceptionList from '../components/time-conditions/ExceptionList';
import ExceptionForm from '../components/time-conditions/ExceptionForm';

type ModalType = 'create' | 'edit' | 'exceptions' | 'createException' | 'editException' | '';

interface ModalState {
  type: ModalType;
  title?: string;
}

function TimeConditions() {
  const [modal, setModal] = useState<ModalState>({ type: '' });
  const [editingCondition, setEditingCondition] = useState<TimeCondition | null>(null);
  const [editingException, setEditingException] = useState<TimeException | null>(null);
  const queryClient = useQueryClient();

  // Queries
  const { data: conditions, isLoading } = useQuery({
    queryKey: ['time-conditions'],
    queryFn: () => timeConditionService.getTimeConditions(),
  });

  const { data: exceptions, isLoading: isLoadingExceptions } = useQuery({
    queryKey: ['time-exceptions'],
    queryFn: () => timeConditionService.getExceptions(),
    enabled: modal.type === 'exceptions' || modal.type === 'editException',
  });

  // Mutations for Time Conditions
  const createMutation = useMutation({
    mutationFn: (data: Partial<TimeCondition>) => timeConditionService.createTimeCondition(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['time-conditions'] });
      messageHandler.success('Condição de tempo criada com sucesso!');
      closeModal();
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<TimeCondition> }) =>
      timeConditionService.updateTimeCondition(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['time-conditions'] });
      messageHandler.success('Condição de tempo atualizada com sucesso!');
      closeModal();
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => timeConditionService.deleteTimeCondition(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['time-conditions'] });
      messageHandler.success('Condição de tempo excluída com sucesso!');
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  // Mutations for Exceptions
  const createExceptionMutation = useMutation({
    mutationFn: (data: Partial<TimeException>) => timeConditionService.createException(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['time-exceptions'] });
      messageHandler.success('Exceção criada com sucesso!');
      setModal({ type: 'exceptions' });
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const updateExceptionMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<TimeException> }) =>
      timeConditionService.updateException(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['time-exceptions'] });
      messageHandler.success('Exceção atualizada com sucesso!');
      setModal({ type: 'exceptions' });
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const deleteExceptionMutation = useMutation({
    mutationFn: (id: number) => timeConditionService.deleteException(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['time-exceptions'] });
      messageHandler.success('Exceção excluída com sucesso!');
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  // Handlers for Time Conditions
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    // Extract basic data
    const data: Partial<TimeCondition> = {
      description: formData.get('description') as string,
      active: formData.get('active') === 'on',
      time_ranges: [],
    };

    // Extract time ranges
    const formEntries = Array.from(formData.entries());

    // Group time range entries
    const timeRangeIndices = new Set<number>();

    // Get all indices
    formEntries.forEach(([key]) => {
      const timeRangeMatch = key.match(/time_ranges\[(\d+)\]/);
      if (timeRangeMatch) {
        timeRangeIndices.add(parseInt(timeRangeMatch[1]));
      }
    });

    // Create time range objects
    Array.from(timeRangeIndices).forEach(index => {
      const id = formData.get(`time_ranges[${index}].id`);
      const weekday = parseInt(formData.get(`time_ranges[${index}].weekday`) as string);
      const startTime = formData.get(`time_ranges[${index}].start_time`) as string;
      const endTime = formData.get(`time_ranges[${index}].end_time`) as string;

      if (!isNaN(weekday) && startTime && endTime) {
        data.time_ranges!.push({
          id: id ? Number(id) : undefined,
          weekday,
          start_time: startTime,
          end_time: endTime,
        });
      }
    });

    if (editingCondition) {
      updateMutation.mutate({ id: editingCondition.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  // Handlers for Exceptions
  const handleExceptionSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    const data: Partial<TimeException> = {
      exception_day: parseInt(formData.get('exception_day') as string),
      exception_month: parseInt(formData.get('exception_month') as string),
      exception_year:
        formData.get('exception_year') && formData.get('exception_year') !== ''
          ? parseInt(formData.get('exception_year') as string)
          : null,
      description: formData.get('description') as string,
      is_available: formData.get('is_available') === 'on',
    };

    if (editingException) {
      updateExceptionMutation.mutate({ id: editingException.id!, data });
    } else {
      createExceptionMutation.mutate(data);
    }
  };

  const handleDelete = (condition: TimeCondition) => {
    messageHandler.confirm(
      'Excluir Condição de Tempo',
      'Tem certeza que deseja excluir esta condição de tempo? Esta ação não pode ser desfeita.',
      () => deleteMutation.mutate(condition.id)
    );
  };

  const handleDeleteException = (exception: TimeException) => {
    messageHandler.confirm(
      'Excluir Exceção',
      'Tem certeza que deseja excluir esta exceção? Esta ação não pode ser desfeita.',
      () => deleteExceptionMutation.mutate(exception.id!)
    );
  };

  const closeModal = () => {
    setModal({ type: '' });
    setEditingCondition(null);
    setEditingException(null);
  };

  return (
    <div className="py-6">
      <div className="px-4 sm:px-6 lg:px-8">
        <PageHeader
          title="Condições de Tempo"
          description="Gerencie quando seus assistentes estarão disponíveis para atendimento."
          actions={
            <div className="flex space-x-3">
              <Button variant="secondary" onClick={() => setModal({ type: 'exceptions' })}>
                <Calendar className="h-4 w-4 mr-2" />
                Gerenciar Exceções
              </Button>
              <Button
                onClick={() => {
                  setEditingCondition(null);
                  setModal({ type: 'create' });
                }}
              >
                <Plus className="h-4 w-4 mr-2" />
                Nova Condição
              </Button>
            </div>
          }
        />

        <TimeConditionList
          conditions={conditions || []}
          onEdit={condition => {
            setEditingCondition(condition);
            setModal({ type: 'edit' });
          }}
          onDelete={handleDelete}
          isLoading={isLoading}
        />

        {/* Time Condition Modal */}
        <Modal
          isOpen={modal.type === 'create' || modal.type === 'edit'}
          onClose={closeModal}
          title={editingCondition ? 'Editar Condição de Tempo' : 'Nova Condição de Tempo'}
          size="xl"
        >
          <TimeConditionForm
            condition={editingCondition}
            onSubmit={handleSubmit}
            isLoading={createMutation.isPending || updateMutation.isPending}
            onClose={closeModal}
          />
        </Modal>

        {/* Exceptions List Modal */}
        <Modal
          isOpen={modal.type === 'exceptions'}
          onClose={closeModal}
          title="Gerenciar Exceções (Feriados e Datas Especiais)"
          size="xl"
        >
          <div className="mb-4">
            <Button
              onClick={() => {
                setEditingException(null);
                setModal({ type: 'createException' });
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Nova Exceção
            </Button>
          </div>

          <ExceptionList
            exceptions={exceptions || []}
            onEdit={exception => {
              setEditingException(exception);
              setModal({ type: 'editException' });
            }}
            onDelete={handleDeleteException}
            isLoading={isLoadingExceptions}
          />

          <div className="mt-4">
            <Button variant="secondary" onClick={closeModal} fullWidth>
              Fechar
            </Button>
          </div>
        </Modal>

        {/* Exception Form Modal */}
        <Modal
          isOpen={modal.type === 'createException' || modal.type === 'editException'}
          onClose={() => setModal({ type: 'exceptions' })}
          title={editingException ? 'Editar Exceção' : 'Nova Exceção'}
        >
          <ExceptionForm
            exception={editingException}
            onSubmit={handleExceptionSubmit}
            isLoading={createExceptionMutation.isPending || updateExceptionMutation.isPending}
            onClose={() => setModal({ type: 'exceptions' })}
          />
        </Modal>
      </div>
    </div>
  );
}

export default TimeConditions;
